import React, { useState } from "react";
import {
    Box,
    Flex,
    Button,
    Input,
    NumberInput,
    NumberInputField,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useColorModeValue,
} from "@chakra-ui/react";
import BeatLoader from "react-spinners/BeatLoader";
import Card from "components/card/Card.js";
import { QuickAlert } from "theme/components/alerts";
import { userController } from "lib/user";

export default function LaundryCreateNew() {
    const { setAlert, clearAlert, showAlert } = QuickAlert();
    const { saveRecord } = userController();

    const [input, setInput] = useState({ date_from: "", date_to: "", margin: "" });
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);

    const headerBorderColor = useColorModeValue("#E6ECFA", "rgba(135, 140, 189, 0.3)");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    const totalRowBg = useColorModeValue("gray.50", "whiteAlpha.50");
    const thColor = useColorModeValue("secondaryGray.700", "white");
    const totalColor = useColorModeValue("gray.700", "white");
    const stickyBg = useColorModeValue("white", "navy.800");

    const handleInput = (e) => {
        const { name, value } = e.target;
        setInput((prev) => ({ ...prev, [name]: value }));
    };

    const handleGenerate = () => {
        clearAlert();
        setResults(null);
        setLoading(true);

        saveRecord("/user/laundry/order/result", input)
            .then(response => {
                setResults(response.data);
                setLoading(false);
            })
            .catch(error => {
                setAlert("error", error.response.data.errors);
                setLoading(false);
            });
    };

    return (
        <Box>
            <Card mb="20px" p="0">
                <Flex justify="space-between" align="center" pt="20px" pb="16px" px="24px" borderBottom="1px solid" borderColor={headerBorderColor}>
                    <Text color="secondaryGray.900" fontSize="22px" fontWeight="700" lineHeight="100%">
                        Calcola Biancheria
                    </Text>
                </Flex>
                <Box px="24px" pt="20px" pb="24px">
                    {showAlert()}
                    <Flex gap="16px" align="flex-end" direction={{ base: "column", md: "row" }}>
                        <Box flex="1">
                            <Text fontSize="sm" fontWeight="500" mb="8px">Data Da</Text>
                            <Input
                                type="date"
                                name="date_from"
                                value={input.date_from}
                                onChange={handleInput}
                            />
                        </Box>
                        <Box flex="1">
                            <Text fontSize="sm" fontWeight="500" mb="8px">Data A</Text>
                            <Input
                                type="date"
                                name="date_to"
                                value={input.date_to}
                                onChange={handleInput}
                            />
                        </Box>
                        <Box flex="1">
                            <Text fontSize="sm" fontWeight="500" mb="8px">Margine</Text>
                            <NumberInput
                                min={0}
                                value={input.margin}
                                onChange={(val) => setInput((prev) => ({ ...prev, margin: val }))}
                            >
                                <NumberInputField name="margin" placeholder="0" />
                            </NumberInput>
                        </Box>
                        <Box>
                            <Button
                                variant="brand"
                                minWidth="130px"
                                isLoading={loading}
                                onClick={handleGenerate}
                            >
                                Genera
                            </Button>
                        </Box>
                    </Flex>
                </Box>
            </Card>

            {loading && (
                <Flex justify="center" py="40px">
                    <BeatLoader loading={true} size={15} aria-label="Loading Spinner" color="#422CFA" />
                </Flex>
            )}

            {!loading && results && results.map((warehouse, wIndex) => (
                <Card key={wIndex} mb="20px" p="0">
                    <Flex justify="space-between" align="center" pt="20px" pb="16px" px="24px" borderBottom="1px solid" borderColor={headerBorderColor}>
                        <Text color="secondaryGray.900" fontSize="22px" fontWeight="700" lineHeight="100%">
                            {warehouse.warehouse}
                        </Text>
                    </Flex>
                    <Box overflowX="auto" width="100%" px="24px" pt="20px" pb="24px">
                        <Table variant="simple" size="sm" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                            <Thead>
                                <Tr>
                                    <Th
                                        color={thColor}
                                        borderColor={borderColor}
                                        fontSize="sm"
                                        textTransform="none"
                                        letterSpacing="normal"
                                        minW="180px"
                                        position="sticky"
                                        left="0"
                                        zIndex="1"
                                        bg={stickyBg}
                                    >
                                        Proprietà
                                    </Th>
                                    {warehouse.features.map((feature, fIndex) => (
                                        <Th
                                            key={fIndex}
                                            color={thColor}
                                            borderColor={borderColor}
                                            textAlign="center"
                                            fontSize="sm"
                                            textTransform="none"
                                            letterSpacing="normal"
                                            minW="120px"
                                            whiteSpace="nowrap"
                                        >
                                            {feature}
                                        </Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {warehouse.rows.map((row, rIndex) => (
                                    <Tr key={rIndex}>
                                        <Td
                                            borderColor={borderColor}
                                            fontSize="sm"
                                            position="sticky"
                                            left="0"
                                            zIndex="1"
                                            bg={stickyBg}
                                            whiteSpace="nowrap"
                                        >
                                            {row.property}
                                        </Td>
                                        {warehouse.features.map((feature, fIndex) => (
                                            <Td
                                                key={fIndex}
                                                borderColor={borderColor}
                                                textAlign="center"
                                                fontSize="sm"
                                            >
                                                {row[feature] ?? "-"}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                                <Tr bg={totalRowBg}>
                                    <Td
                                        borderColor={borderColor}
                                        borderTop="2px solid"
                                        borderTopColor={borderColor}
                                        fontWeight="bold"
                                        fontSize="sm"
                                        color={totalColor}
                                        position="sticky"
                                        left="0"
                                        zIndex="1"
                                        bg={totalRowBg}
                                    >
                                        Totale
                                    </Td>
                                    {warehouse.features.map((feature, fIndex) => (
                                        <Td
                                            key={fIndex}
                                            borderColor={borderColor}
                                            borderTop="2px solid"
                                            borderTopColor={borderColor}
                                            textAlign="center"
                                            fontWeight="bold"
                                            fontSize="sm"
                                            color={totalColor}
                                        >
                                            {warehouse.total[feature] ?? "-"}
                                        </Td>
                                    ))}
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>
                </Card>
            ))}
        </Box>
    );
}
